/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { FormControl, Grid, MenuItem } from '@mui/material';
import { CustomIconButton, TooltipSelect } from '@opengeoweb/shared';
import { Overlayers } from '@opengeoweb/theme';
import { useCoreTranslation } from '../../../utils/i18n';
import { columnClasses, leftButtonsStyle } from '../LayerManagerUtils';

export interface OverLayer {
  id: string;
  name: string;
}

interface OverlayersSelectProps {
  mapId: string;
  selectedOverlayer: OverLayer;
  onChangeOverlayer: (mapId: string, selectedOverlayer: OverLayer) => void;
  availableOverlayers?: { id: string; name: string }[];
}

const OverlayersSelect: React.FC<OverlayersSelectProps> = ({
  mapId,
  selectedOverlayer,
  onChangeOverlayer,
  availableOverlayers,
}: OverlayersSelectProps) => {
  const { t } = useCoreTranslation();

  const handleOverlayerChange = (value: string): void => {
    const selectedOverlayer = availableOverlayers?.find(
      (layer) => layer.name === value,
    );
    if (selectedOverlayer) {
      onChangeOverlayer(mapId, selectedOverlayer);
    }
  };

  const overlayerOptions = availableOverlayers?.map((layer) => ({
    value: layer.name,
  }));

  const currentIndex = availableOverlayers?.findIndex(
    (layer) => layer.name === selectedOverlayer.name,
  );

  return (
    <Grid
      className="overlayers-column"
      container
      sx={{
        width: '100%',
        height: '36px',
      }}
    >
      <Grid sx={[{ marginTop: '-2px' }, leftButtonsStyle]}>
        <CustomIconButton
          disabled
          sx={{
            margin: 'auto 0px auto 32px',
          }}
        >
          <Overlayers />
        </CustomIconButton>
      </Grid>
      <Grid className={columnClasses.baseLayer}>
        <FormControl style={{ width: '100%' }}>
          <TooltipSelect
            SelectDisplayProps={{ 'aria-label': 'overlayerSelect' }}
            currentIndex={currentIndex}
            disableUnderline
            isEnabled
            list={overlayerOptions}
            onChange={(event): void => {
              event.stopPropagation();
              handleOverlayerChange(event.target.value as string);
            }}
            onChangeMouseWheel={(value): void => {
              handleOverlayerChange(value.value);
            }}
            style={{ maxWidth: '100%' }}
            tooltip={`${t('layermanager-overlayers-overlayer')}: ${selectedOverlayer.name}`}
            value={selectedOverlayer.name}
          >
            <MenuItem disabled>{t('layermanager-overlayers-title')}</MenuItem>
            {availableOverlayers?.map(({ id, name }) => (
              <MenuItem key={id} value={name}>
                {name}
              </MenuItem>
            ))}
          </TooltipSelect>
        </FormControl>
      </Grid>
    </Grid>
  );
};

export default OverlayersSelect;
